import { useEffect, useRef, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { preloadRouteChunk } from "@/config/routes";
import {
	getAccountDeletionInProgressDetail,
	getEmailVerificationDetail,
	refreshAccessToken,
	syncCurrentUser,
} from "@/lib/auth";
import { getTokenSubject, useAuthStore } from "@/stores/useAuthStore";

export default function RequireAuth() {
	const location = useLocation();
	const accessToken = useAuthStore((state) => state.accessToken);
	const user = useAuthStore((state) => state.user);
	const emailVerificationRequired = useAuthStore(
		(state) => state.emailVerificationRequired,
	);
	const setUser = useAuthStore((state) => state.setUser);
	const setPendingVerification = useAuthStore(
		(state) => state.setPendingVerification,
	);
	const reset = useAuthStore((state) => state.reset);
	const [isBootstrapping, setIsBootstrapping] = useState(
		Boolean(accessToken) && !user,
	);
	const validatedTokenRef = useRef<string | null>(null);

	useEffect(() => {
		preloadRouteChunk(location.pathname);
	}, [location.pathname]);

	useEffect(() => {
		if (!accessToken || emailVerificationRequired) {
			validatedTokenRef.current = null;
			setIsBootstrapping(false);
			return;
		}

		if (validatedTokenRef.current === accessToken) {
			setIsBootstrapping(false);
			return;
		}

		let cancelled = false;
		if (!user) setIsBootstrapping(true);

		const validateSession = async () => {
			try {
				const refreshedToken = await refreshAccessToken();
				if (!refreshedToken) {
					if (!cancelled) reset();
					return;
				}
				const syncedUser = await syncCurrentUser(refreshedToken);
				if (cancelled) return;
				if (useAuthStore.getState().accessToken !== refreshedToken) return;
				validatedTokenRef.current = refreshedToken;
				setUser(syncedUser);
			} catch (error) {
				if (cancelled) return;
				if (getAccountDeletionInProgressDetail(error)) {
					reset();
				} else {
					const verification = getEmailVerificationDetail(error);
					if (verification) {
						setPendingVerification(verification.email ?? user?.email ?? null);
					} else {
						reset();
					}
				}
			} finally {
				if (!cancelled) setIsBootstrapping(false);
			}
		};

		validateSession();
		return () => {
			cancelled = true;
		};
	}, [
		accessToken,
		emailVerificationRequired,
		reset,
		setPendingVerification,
		setUser,
		user,
	]);

	if (!accessToken) {
		return <Navigate to="/auth/login" replace state={{ from: location }} />;
	}

	if (emailVerificationRequired) {
		return <Navigate to="/auth/verify-email" replace />;
	}

	const isValidatedForCurrentToken = validatedTokenRef.current === accessToken;
	const tokenSubject = getTokenSubject(accessToken);
	const isUserForCurrentToken = user?.auth0_sub === tokenSubject;

	if (
		isBootstrapping ||
		!isValidatedForCurrentToken ||
		!isUserForCurrentToken
	) {
		return (
			<div className="flex min-h-screen items-center justify-center bg-background">
				<div className="h-8 w-8 animate-spin rounded-full border-2 border-marina-500 border-t-transparent" />
			</div>
		);
	}

	if (!user) {
		return <Navigate to="/auth/login" replace state={{ from: location }} />;
	}

	const onOnboardingRoute = location.pathname === "/onboarding";
	if (user.company_id == null && !onOnboardingRoute) {
		return <Navigate to="/onboarding" replace />;
	}
	if (user.company_id != null && onOnboardingRoute) {
		return <Navigate to="/" replace />;
	}

	return <Outlet />;
}
